/**
 * Permission utility functions for checking user access
 * Works with React Admin's permission format: { resource: { action: scope | boolean } }
 *
 * Values are either a PermissionScope string ("partner" | "assigned") or boolean `true`
 * for backward compatibility. All truthy values grant the permission; scope strings
 * additionally encode how broadly the permission applies.
 */

import { ValidAny } from "@/types";
import type { PermissionScope } from "@/types/permission";

type Permissions = Record<string, ValidAny> | undefined | null;

export const hasPermission = (permissions: Permissions, resource: string, action: string): boolean => {
  return !!permissions?.[resource]?.[action];
};

export const canList = (permissions: Permissions, resource: string) => hasPermission(permissions, resource, "list");

export const canShow = (permissions: Permissions, resource: string) => hasPermission(permissions, resource, "show");

export const canCreate = (permissions: Permissions, resource: string) => hasPermission(permissions, resource, "create");

export const canEdit = (permissions: Permissions, resource: string) => hasPermission(permissions, resource, "edit");

export const canDelete = (permissions: Permissions, resource: string) => hasPermission(permissions, resource, "delete");

// Check if user has ANY permission for a resource (useful for menu visibility)
export const hasAnyPermission = (permissions: Permissions, resource: string): boolean => {
  return !!permissions?.[resource] && Object.keys(permissions[resource]).length > 0;
};

/**
 * The server-enforced destructive-cascade capability for documents (#5752).
 * Exposed through the permissions system as the `documents:cascade-delete`
 * capability, which arrives in the react-admin permissions object as the
 * action `cascade-delete` under the `documents` resource. Kept as a named
 * constant so the string lives in exactly one place (the permission key must
 * stay in lock-step with the backend grant).
 */
export const CASCADE_DELETE_ACTION = "cascade-delete";

/**
 * True only when the user holds the `documents:cascade-delete` capability.
 * This is a distinct, server-enforced grant — NOT implied by `documents:delete`
 * (single-document delete). The cascade button gates on this in addition to the
 * kill-switch feature flag and the not-blocked preview.
 */
export const hasCascadeDeleteCapability = (permissions: Permissions): boolean =>
  hasPermission(permissions, "documents", CASCADE_DELETE_ACTION);

// --- Scope-aware utilities ---

/** Returns the effective scope for a permission, or null if the permission is absent. */
export const getScope = (permissions: Permissions, resource: string, action: string): PermissionScope | null => {
  const value = permissions?.[resource]?.[action];
  if (!value) return null;
  if (value === true) return "partner"; // backward compat: old format stored booleans
  if (value === "partner" || value === "assigned") return value;
  return null;
};

/** Returns true only if the user has partner (unrestricted) scope for this permission. */
export const hasPartnerScope = (permissions: Permissions, resource: string, action: string): boolean =>
  getScope(permissions, resource, action) === "partner";

/**
 * Maps resource names to the field that identifies the record's assigned user.
 * Used by canActOnRecord to evaluate "assigned" scope.
 */
const OWNERSHIP_FIELDS: Partial<Record<string, string>> = {
  tasks: "assigned_user_id_ref",
};

/**
 * Checks if a user can perform an action on a specific record given their scope.
 * - "partner" scope: allowed on any record in their partner
 * - "assigned" scope: allowed only if the record's ownership field matches currentUserId
 * - No permission: denied
 */
export const canActOnRecord = (
  permissions: Permissions,
  resource: string,
  action: string,
  record: Record<string, unknown> | undefined | null,
  currentUserId: string | undefined
): boolean => {
  const scope = getScope(permissions, resource, action);
  if (!scope) return false;
  if (scope === "partner") return true;
  // scope === "assigned" — check record ownership
  const field = OWNERSHIP_FIELDS[resource];
  if (!field || !record || !currentUserId) return false;
  return record[field] === currentUserId;
};
